import clsx from 'clsx'
import stylesNav from 'components/Nav/nav.module.scss'
import format from 'date-fns/format'
import { Grid, Icon, Tooltip, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { formatToString } from 'utils/formatMoney'
import { useWalletsActions } from 'modules/Finance/Wallets/useWalletsActions'
import { useWallets } from 'modules/Finance/Wallets/useWallets'
import Price from '../Price'
import StatusLabel from '../StatusLabel'
import WalletListSkeleton from 'modules/Finance/Wallets/subcomponents/WalletsList/WalletListSkeleton'
import styles from 'modules/Finance/Wallets/subcomponents/WalletsList/WalletsList.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'

const WalletsList = ({ className = '' }) => {
  const { i18n } = useSelector(({ app }) => app)
  const { t } = useTranslation(['wallets'])
  const listRef = useRef()
  const pathGen = useGeneratePathI18n()

  const {
    //resetWallets,
    getWallets,
    setActiveWalletID,
    fetchExpiredPromisedPayments,
  } = useWalletsActions()

  const {
    wallets: { data: wallets, loading },
  } = useWallets()

  useEffect(() => {
    getWallets()
  }, [i18n])

  useEffect(() => {
    fetchExpiredPromisedPayments({ params: { is_expired: true } })

    // return () => {
    //   resetWallets()
    // }
  }, [])

  return loading ? (
    <div className={className}>
      <WalletListSkeleton className={styles['wallets-list-placeholder']} />
    </div>
  ) : (
    wallets.length > 0 && (
      <nav
        ref={listRef}
        className={clsx(styles['wallets-list'], stylesNav['nav'])}
      >
        <Typography
          component={'div'}
          variant={'Body-10'}
          color={'Label-Tertiary'}
          className={stylesNav['group-header']}
        >
          {t('wallets::nav.wallets_header')}
        </Typography>
        <div className={styles['wallets-list__list']}>
          {wallets.map((wallet) => {
            const {
              id,
              currency,
              balance,
              financial_profile: {
                financial_profile_type: { type = null },
                custom_name,
              } = {},
              promisedPayment = null,
            } = wallet
            return (
              <div key={id} className={clsx(styles['wallets-list__item'])}>
                <NavLink
                  title={`${
                    custom_name ?? t('wallets::types.' + type)
                  }: ${balance} ${currency}`}
                  to={pathGen(ROUTES.FINANCE_WALLET, {
                    walletID: id,
                  })}
                  className={clsx(
                    stylesNav['nav__link'],
                    styles['wallets-list__item'],
                    {
                      [styles['wallets-list__item_expired']]:
                        promisedPayment && promisedPayment.is_expired,
                      [styles['wallets-list__item_taken']]:
                        promisedPayment && !promisedPayment.is_expired,
                    },
                  )}
                  activeClassName={stylesNav['nav__link_active']}
                  onClick={() => setActiveWalletID(id)}
                >
                  <div
                    className={clsx(
                      'icon',
                      styles['wallets-list__icon'],
                      styles[`wallets-list__icon_${currency.toLowerCase()}`],
                    )}
                  >
                    <Icon
                      color="Interface-Onsurface"
                      name={currency.toLowerCase()}
                      width={20}
                    />
                  </div>
                  <Grid
                    className={styles['wallets-list__context']}
                    direction={'column'}
                    gap={4}
                  >
                    <Typography
                      variant={'Body-25'}
                      className={styles['wallets-list__cost']}
                    >
                      <Price
                        className={styles['wallets-list__cost-number']}
                        number={balance}
                        currency={currency}
                      />
                      {promisedPayment && !promisedPayment.is_expired && (
                        <div className={styles['wallets-list__status-icon']}>
                          <Tooltip
                            title={`${t('nav.payment.taken')} ${formatToString(
                              promisedPayment.repayment_amount,
                              currency,
                            )} ${t('nav.by')}  ${format(
                              promisedPayment.repayment_date * 1000,
                              t('date_format.compact'),
                            )}`}
                            arrow
                            placement="right"
                          >
                            <Icon name="status pause" width={16} height={16} />
                          </Tooltip>
                        </div>
                      )}
                      {promisedPayment && promisedPayment.is_expired && (
                        <div className={styles['wallets-list__status-icon']}>
                          <Tooltip
                            title={`${t(
                              'nav.payment.expired',
                            )} ${formatToString(
                              promisedPayment.repayment_amount,
                              currency,
                            )}`}
                            arrow
                            placement="right"
                          >
                            <Icon
                              name="status warning"
                              color="secondary-46"
                              width={16}
                              height={16}
                            />
                          </Tooltip>
                        </div>
                      )}
                    </Typography>
                    {promisedPayment && !promisedPayment.is_expired && (
                      <StatusLabel className={styles['wallets-list__label']}>
                        {t('types.taken')}
                      </StatusLabel>
                    )}
                    {promisedPayment && promisedPayment.is_expired && (
                      <StatusLabel className={styles['wallets-list__label']}>
                        {t('types.expired')}
                      </StatusLabel>
                    )}
                    <Typography
                      variant={'Body-10'}
                      className={styles['wallets-list__summary']}
                    >
                      {custom_name ?? t(`wallets::types.${type}`)}
                    </Typography>
                  </Grid>
                </NavLink>
              </div>
            )
          })}
        </div>
      </nav>
    )
  )
}

export default WalletsList

WalletsList.propTypes = {
  className: PropTypes.string,
}
